/**
 * Column Width Calculation
 *
 * Calculates appropriate column widths based on field metadata.
 * Uses py_type and field characteristics to determine sizing.
 */

import type { FieldMetadata } from '@/services/metadataService'

/**
 * Default column widths by data type (in pixels)
 */
const TYPE_WIDTHS: Record<string, number> = {
  // Identifiers
  uuid: 120,

  // Text types
  string: 150,
  str: 150,
  text: 200,

  // Numeric types
  integer: 100,
  int: 100,
  numeric: 120,
  float: 120,
  decimal: 120,

  // Boolean
  bool: 80,
  boolean: 80,

  // Date/Time
  date: 110,      // "Jan 12, 2026" fits in 110px
  datetime: 180,  // "Dec 22, 2025, 10:22 AM" needs ~180px
  timestamp: 180,
  timestamptz: 180,
  time: 80,       // "2:30 PM" is short
}

/**
 * Width adjustments for specific field characteristics
 */
const CHARACTERISTIC_ADJUSTMENTS = {
  /** Fields with max_length > 100 get more space */
  longText: 250,
  /** Fields marked as textarea need more space */
  textarea: 300,
  /** ID/key fields are typically shorter */
  identifier: 120,
  /** Status/state fields are usually short */
  status: 100,
}

/**
 * Calculate column width based on field metadata.
 *
 * @param fieldKey - The field key name
 * @param metadata - Field metadata
 * @returns Width in pixels
 */
export function calculateColumnWidth(
  fieldKey: string,
  metadata: FieldMetadata
): number {
  // Check for component_type hints
  if (metadata.component_type === 'textarea') {
    return CHARACTERISTIC_ADJUSTMENTS.textarea
  }

  // Check for long text fields
  if (metadata.max_length && metadata.max_length > 100) {
    return CHARACTERISTIC_ADJUSTMENTS.longText
  }

  // Check for identifier patterns
  const lowerKey = fieldKey.toLowerCase()
  if (
    lowerKey.endsWith('id') ||
    lowerKey === 'sku' ||
    lowerKey === 'code' ||
    lowerKey === 'key'
  ) {
    return CHARACTERISTIC_ADJUSTMENTS.identifier
  }

  // Check for status/state patterns
  if (
    lowerKey.includes('status') ||
    lowerKey.includes('state') ||
    lowerKey.includes('type')
  ) {
    return CHARACTERISTIC_ADJUSTMENTS.status
  }

  // Use py_type based width
  const pyType = (metadata.py_type || 'string').toLowerCase()
  return TYPE_WIDTHS[pyType] ?? TYPE_WIDTHS.string
}

/**
 * Calculate minimum column width (for resizing, if implemented)
 */
export function calculateMinColumnWidth(metadata: FieldMetadata): number {
  const pyType = (metadata.py_type || 'string').toLowerCase()

  // Boolean/checkbox columns have fixed min width
  if (pyType === 'bool' || pyType === 'boolean') {
    return 60
  }

  // Date columns need minimum space
  if (pyType.includes('date') || pyType.includes('time')) {
    return 100
  }

  // General minimum
  return 80
}

/**
 * Check if column should have ellipsis overflow
 */
export function shouldEllipsis(metadata: FieldMetadata): boolean {
  const pyType = (metadata.py_type || 'string').toLowerCase()

  // Text fields can overflow
  if (pyType === 'string' || pyType === 'str' || pyType === 'text') {
    return true
  }

  // Textarea definitely needs ellipsis
  if (metadata.component_type === 'textarea') {
    return true
  }

  return false
}
